import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export const CHAT_PANEL_MODE_KEY = "chat-panel-open";

type ChatPanelContextType = {
  isOpen: boolean;
  currentThreadId: string | null;
  pendingMessage: string | null;
  setIsOpen: (open: boolean) => void;
  triggerNewChat: () => void;
  openThread: (threadId: string) => void;
  syncThreadId: (threadId: string | null) => void;
  clearThread: () => void;
  sendMessage: (text: string) => void;
  clearPendingMessage: () => void;
};

const ChatPanelContext = createContext<ChatPanelContextType | null>(null);

export function ChatPanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpenState] = useState(() => {
    const stored = localStorage.getItem(CHAT_PANEL_MODE_KEY);
    return stored === "true";
  });
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  const [currentThreadId, setCurrentThreadIdState] = useState<string | null>(
    () => {
      return localStorage.getItem("threadId");
    },
  );

  useEffect(() => {
    localStorage.setItem(CHAT_PANEL_MODE_KEY, String(isOpen));
  }, [isOpen]);

  const setIsOpen = useCallback((open: boolean) => {
    setIsOpenState(open);
  }, []);

  const setCurrentThreadId = useCallback((id: string | null) => {
    if (id) {
      localStorage.setItem("threadId", id);
    } else {
      localStorage.removeItem("threadId");
    }
    setCurrentThreadIdState(id);
  }, []);

  const triggerNewChat = useCallback(() => {
    setCurrentThreadId(null);
    setIsOpen(true);
  }, [setCurrentThreadId, setIsOpen]);

  const openThread = useCallback(
    (threadId: string) => {
      if (isOpen && currentThreadId === threadId) return;
      setCurrentThreadId(threadId);
      setIsOpen(true);
    },
    [isOpen, currentThreadId, setCurrentThreadId, setIsOpen],
  );

  const syncThreadId = useCallback(
    (threadId: string | null) => {
      setCurrentThreadId(threadId);
    },
    [setCurrentThreadId],
  );

  const clearThread = useCallback(() => {
    setCurrentThreadId(null);
  }, [setCurrentThreadId]);

  const sendMessage = useCallback(
    (text: string) => {
      setCurrentThreadId(null);
      setIsOpen(true);
      setPendingMessage(text);
    },
    [setCurrentThreadId, setIsOpen],
  );

  const clearPendingMessage = useCallback(() => {
    setPendingMessage(null);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      triggerNewChat,
      openThread,
      currentThreadId,
      syncThreadId,
      clearThread,
      sendMessage,
      pendingMessage,
      clearPendingMessage,
    }),
    [
      isOpen,
      setIsOpen,
      triggerNewChat,
      openThread,
      currentThreadId,
      syncThreadId,
      clearThread,
      sendMessage,
      pendingMessage,
      clearPendingMessage,
    ],
  );

  return (
    <ChatPanelContext.Provider value={value}>
      {children}
    </ChatPanelContext.Provider>
  );
}

export function useChatPanel() {
  const ctx = useContext(ChatPanelContext);
  if (!ctx)
    throw new Error("useChatPanel must be used within ChatPanelProvider");
  return ctx;
}
